/**
 * QuestionTabPanel Component
 *
 * Main editor panel for a single question within a daily exercise.
 * Handles all question fields, question type-specific UI, and integrates
 * ComprehensionEditor and QuestionAssetManager components.
 */

import React from 'react';
import {Input, Select, Button, Popconfirm, Typography} from 'antd';
import type {QuestionWithNewFields} from '../../Questions/QuestionTypes';
import {QUESTION_TYPE, TestTypes} from '../../../graphql/API';
import {ComprehensionEditor} from './ComprehensionEditor';
import {QuestionAssetManager} from './QuestionAssetManager';
import {QUESTION_TYPE_DICT} from '../QuestionTypeDict';
import {BackendQuestionType} from '../../../types/newQuestionType';
import {getQuestionStaticValues} from '../../../utils/staticValuesUtils/questionStaticValues';

const {Text} = Typography;

export interface QuestionTabPanelProps {
  question: QuestionWithNewFields;
  questionIndex: number;
  testType: TestTypes | undefined;
  programsList: any[];

  // Question update handlers
  onQuestionUpdate: (updatedQuestion: QuestionWithNewFields) => void;
  onDeleteQuestion: () => void;

  // Comprehension-specific handlers (using payload.blanks structure)
  selectedBlankIndex: number;
  onSelectedBlankIndexChange: (index: number) => void;
  onTextChange: (newText: string, isAIText?: boolean) => void;
  onTextBlur: () => void;
  onComprehensionChoiceValueChange: (
    blankIndex: number,
    choiceIndex: number,
    value: string,
  ) => void;
  onComprehensionCorrectAnswerChange: (
    blankIndex: number,
    correctAnswer: string,
  ) => void;
  onComprehensionAddChoice: (blankIndex: number) => void;

  // Loading states
  isDeleting?: boolean;
}

/**
 * Panel component for editing a single question
 */
export const QuestionTabPanel: React.FC<QuestionTabPanelProps> = ({
  question,
  questionIndex,
  testType,
  programsList,
  onQuestionUpdate,
  onDeleteQuestion,
  selectedBlankIndex,
  onSelectedBlankIndexChange,
  onTextChange,
  onTextBlur,
  onComprehensionChoiceValueChange,
  onComprehensionCorrectAnswerChange,
  onComprehensionAddChoice,
  isDeleting = false,
}) => {
  /**
   * Helper to update a question field
   */
  const updateField = (field: keyof QuestionWithNewFields, value: any) => {
    onQuestionUpdate({...question, [field]: value});
  };

  /**
   * Auto-update summary and description with static values
   * whenever question type or asset type changes
   */
  React.useEffect(() => {
    // Extract asset type from payload.media (correct source)
    const assetType =
      question?.payload?.media?.[0]?.type || question?.asset?.type || 'default';

    const staticValues = getQuestionStaticValues(question?.type, assetType);

    // Only update if values have changed to avoid infinite loops
    if (
      question.summary !== staticValues.summary ||
      question.description !== staticValues.description
    ) {
      onQuestionUpdate({
        ...question,
        summary: staticValues.summary,
        description: staticValues.description,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.type, question.payload?.media?.[0]?.type, question.asset?.type]);

  /**
   * Handles question type change and initializes type-specific fields
   */
  const handleQuestionTypeChange = (selectedType: QUESTION_TYPE) => {
    const updatedQuestion = {
      ...question,
      type: selectedType,
      text: '',
      textAI: '',
      choices: [],
    };

    // Initialize payload structure for COMPREHENSION type
    if (selectedType === QUESTION_TYPE.COMPREHENSION) {
      updatedQuestion.payload = {
        blanks: [],
        media: updatedQuestion.payload?.media || [],
      };
    } else {
      // Reset payload.blanks for other types
      updatedQuestion.payload = {
        blanks: [],
        media: updatedQuestion.payload?.media || [],
      };
    }

    onQuestionUpdate(updatedQuestion);
  };

  return (
    <div style={{border: '2px solid gray', padding: '10px'}}>
      {/* Delete Question Button */}
      <Popconfirm
        title="The question will be permanently deleted from the database. Are you sure?"
        okText="Yes"
        cancelText="No"
        onConfirm={onDeleteQuestion}>
        <Button
          style={{
            backgroundColor: 'red',
            color: 'white',
            fontWeight: 'bold',
            marginBottom: '20px',
          }}
          loading={isDeleting}>
          Delete question
        </Button>
      </Popconfirm>

      {/* Question Name */}
      <div style={{marginTop: '20px', marginBottom: '20px'}}>
        <Text>Question name</Text>
        <Input
          value={question.name}
          onChange={e => updateField('name', e.target.value)}
          placeholder="Question name"
        />
      </div>

      {/* Question Summary */}
      <div style={{marginTop: '20px', marginBottom: '20px'}}>
        <Text>Question Summary</Text>
        <Input
          value={
            getQuestionStaticValues(question?.type, question?.asset?.type)
              .summary
          }
          onChange={e => updateField('summary', e.target.value)}
          placeholder="Question Summary"
          disabled
        />
      </div>

      {/* Question Description */}
      <div style={{marginTop: '20px', marginBottom: '20px'}}>
        <Text>Question description</Text>
        <Input
          value={
            getQuestionStaticValues(question?.type, question?.asset?.type)
              .description
          }
          onChange={e => updateField('description', e.target.value)}
          placeholder="Question description"
          disabled
        />
      </div>

      {/* Question Type Selector */}
      <div style={{marginTop: '20px', marginBottom: '20px'}}>
        <Text style={{display: 'block'}}>Question type</Text>
        <Select
          value={question.type}
          showSearch
          style={{width: 300}}
          placeholder="Select a question type"
          optionFilterProp="children"
          onChange={handleQuestionTypeChange}>
          <Select.Option value={QUESTION_TYPE.READ_SENTENCE}>
            {QUESTION_TYPE.READ_SENTENCE} (default)
          </Select.Option>
          <Select.Option value={QUESTION_TYPE.CHOICES}>
            {QUESTION_TYPE.CHOICES}
          </Select.Option>
          <Select.Option value={QUESTION_TYPE.MISSING_LETTERS}>
            {QUESTION_TYPE.MISSING_LETTERS}
          </Select.Option>
          <Select.Option value={QUESTION_TYPE.MISSING_WORD}>
            {QUESTION_TYPE.MISSING_WORD}
          </Select.Option>
          <Select.Option value={QUESTION_TYPE.COMPREHENSION}>
            {QUESTION_TYPE.COMPREHENSION}
          </Select.Option>
          <Select.Option value={QUESTION_TYPE.TRANSCRIBE_AND_SCORE}>
            {QUESTION_TYPE.TRANSCRIBE_AND_SCORE}
          </Select.Option>
          <Select.Option value={BackendQuestionType.DESCRIBE_IMAGE}>
            {BackendQuestionType.DESCRIBE_IMAGE}
          </Select.Option>
          <Select.Option value={QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION}>
            {QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION}
          </Select.Option>
        </Select>
      </div>

      {/* Program Selector */}
      <div style={{marginTop: '20px', marginBottom: '20px'}}>
        <Text>Program *</Text>
        <Select
          style={{width: '100%', marginTop: '5px'}}
          placeholder="Select a program"
          value={(() => {
            console.log(
              '🟢 [QuestionTabPanel] question.program value:',
              question.program,
            );
            console.log(
              '🟢 [QuestionTabPanel] Available programs:',
              programsList.map((p: any) => ({id: p.id || p._id, name: p.name})),
            );
            return question.program || undefined;
          })()}
          optionFilterProp="children"
          onChange={(selectedProgramId: string) =>
            updateField('program', selectedProgramId)
          }>
          {programsList.map((program: any, programIndex) => {
            const optionValue = program.id || program._id;
            console.log(
              '🟡 [QuestionTabPanel] Option value:',
              optionValue,
              'matches:',
              optionValue === question.program,
            );
            return (
              <Select.Option value={optionValue} key={programIndex}>
                {program.name}
              </Select.Option>
            );
          })}
        </Select>
      </div>

      {/* Difficulty Selector */}
      <div style={{marginTop: '20px', marginBottom: '20px'}}>
        <Text>Difficulty Level</Text>
        <Select
          style={{width: '100%', marginTop: '5px'}}
          placeholder="Select difficulty"
          value={question.difficulty || 'NORMAL'}
          optionFilterProp="children"
          onChange={(selectedDifficulty: 'EASY' | 'NORMAL' | 'HARD') =>
            updateField('difficulty', selectedDifficulty)
          }>
          <Select.Option value="EASY">Easy</Select.Option>
          <Select.Option value="NORMAL">Normal</Select.Option>
          <Select.Option value="HARD">Hard</Select.Option>
        </Select>
      </div>

      {/* Question Type-Specific Fields */}
      {question.type === QUESTION_TYPE.CHOICES ? (
        // CHOICES: 4 options + question text
        <div>
          {new Array(4).fill(null).map((_, arrayIndex) => (
            <div
              key={`choice-${arrayIndex}`}
              style={{marginTop: '20px', marginBottom: '20px'}}>
              <Text>Choice number {arrayIndex + 1}</Text>
              <Input
                value={question.choices?.[arrayIndex] || ''}
                onChange={e => {
                  const newChoices = [...(question.choices || [])];
                  newChoices[arrayIndex] = e.target.value;
                  updateField('choices', newChoices);
                }}
                placeholder={`Choice number ${arrayIndex + 1}`}
              />
            </div>
          ))}
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Question text</Text>
            <Input
              value={question.text}
              onChange={e => onTextChange(e.target.value)}
              placeholder="Question text"
            />
          </div>
        </div>
      ) : (
        // All other types: Question text input
        <div style={{marginTop: '20px', marginBottom: '20px'}}>
          <Text>Question text</Text>
          <Input
            value={question.text}
            onBlur={onTextBlur}
            onChange={e => onTextChange(e.target.value)}
            placeholder="Question text"
          />
        </div>
      )}

      {/* AI Text Field (for NORMAL_SCORE questions) */}
      {QUESTION_TYPE_DICT[question.type] === 'NORMAL_SCORE' && (
        <div
          style={{
            marginTop: '20px',
            marginBottom: '20px',
            border: '2px solid red',
            padding: '10px',
          }}>
          <Text>Question AI text</Text>
          <Input
            value={question.textAI}
            onBlur={onTextBlur}
            onChange={e => onTextChange(e.target.value, true)}
            placeholder="Question AI text"
          />
        </div>
      )}

      {/* COMPREHENSION Question Editor */}
      {question.type === QUESTION_TYPE.COMPREHENSION && (
        <ComprehensionEditor
          question={question}
          selectedBlankIndex={selectedBlankIndex}
          onSelectedBlankIndexChange={onSelectedBlankIndexChange}
          onChoiceValueChange={onComprehensionChoiceValueChange}
          onCorrectAnswerChange={onComprehensionCorrectAnswerChange}
          onAddChoice={onComprehensionAddChoice}
        />
      )}

      {/* Question Asset Manager */}
      <QuestionAssetManager
        asset={question.asset}
        onAssetChange={asset => updateField('asset', asset)}
        correctAsset={question.correctAsset}
        onCorrectAssetChange={asset => updateField('correctAsset', asset)}
        videoAISessionUri={question.videoAISessionUri}
        onVideoAISessionUriChange={uri => updateField('videoAISessionUri', uri)}
        questionType={question.type}
        testType={testType}
      />

      {/* Display payload.media URLs */}
      {question.payload?.media && question.payload.media.length > 0 && (
        <div
          style={{
            marginTop: '20px',
            padding: '10px',
            border: '1px solid #d9d9d9',
            backgroundColor: '#f5f5f5',
          }}>
          <Text strong>Question Media (payload.media):</Text>
          {question.payload.media.map((mediaItem: any, index: number) => (
            <div key={index} style={{marginTop: '10px'}}>
              <Text>
                Media {index + 1} - Type: {mediaItem.type}
              </Text>
              <br />
              <Text
                type="secondary"
                style={{fontSize: '12px', wordBreak: 'break-all'}}>
                {mediaItem.url}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
